/**
 * Asks before a recruiter's one-off email or voice-agent call when automation
 * already has the next one scheduled, and offers to send that one now.
 *
 * A manual touch before the employer answers turns an automated verification
 * into an assisted one. Sending the scheduled step early gets the recruiter
 * the same "go now" while it still counts as automated; the later automated
 * emails move up to keep their spacing (the API does that). "Send manually" /
 * "Call anyway" still proceeds with the one-off, and Cancel does nothing.
 *
 * The check reads the actions from the server at the moment of the click: the
 * drawer's copy can be minutes old. If the check fails the one-off proceeds,
 * as it did before this prompt existed.
 */
import { useRef, useState } from 'react'

import { useApolloClient, useMutation } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Modal, Text } from '@mantine/core'
import type { EmploymentVerificationAction } from 'types/graphql'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  VoEActionStatus,
  VoEOutreachType,
} from 'src/graphql/types/verificationOfEmployments'
import { GET_VOE_ACTIONS, SEND_VOE_OUTREACH_NOW } from 'src/graphql/voe.graphql'
import { formatDateCustom } from 'src/lib/formatters'

export type ScheduledOutreachKind = 'EMAIL' | 'CALL'
export type ScheduledOutreachOutcome = 'sent_now' | 'manual' | 'cancelled'

type ActionLike = Pick<
  EmploymentVerificationAction,
  'voeActionId' | 'type' | 'status' | 'scheduledAt' | 'configStepOrder'
> & { callAttempt?: number | null }

const DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm A'

/**
 * The next automated email (a config step) or automated call (a call
 * sequence attempt) still waiting to go out. One-off manual rows never count.
 */
export const nextScheduledAutomatedAction = (
  actions: ActionLike[] | null | undefined,
  kind: ScheduledOutreachKind
): ActionLike | null => {
  const pending = (actions ?? []).filter(
    (a) =>
      a.status === VoEActionStatus.SCHEDULED &&
      (kind === 'EMAIL'
        ? a.type === VoEOutreachType.EMAIL && a.configStepOrder != null
        : a.type === VoEOutreachType.CALL && a.callAttempt != null)
  )
  pending.sort(
    (a, b) =>
      new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
  )
  return pending[0] ?? null
}

const COPY = {
  EMAIL: {
    title: 'An automated email is already scheduled',
    sendNow: 'Send scheduled email now',
    manual: 'Send manually',
    sentToast: 'The scheduled email is going out now',
  },
  CALL: {
    title: 'An automated call is already scheduled',
    sendNow: 'Place scheduled call now',
    manual: 'Call anyway',
    sentToast: 'The scheduled call is going out now',
  },
} as const

export const describeScheduledOutreach = ({
  kind,
  scheduledAt,
  savedEmail,
  recipient,
  now = new Date(),
}: {
  kind: ScheduledOutreachKind
  scheduledAt: string | Date
  savedEmail?: string | null
  recipient?: string | null
  now?: Date
}): string => {
  const at = new Date(scheduledAt)
  const when =
    at.getTime() <= now.getTime()
      ? 'is about to go out'
      : `is scheduled for ${formatDateCustom(at, DATE_TIME_FORMAT)}`

  if (kind === 'CALL') {
    return `An automated call to this employer ${when}. Reschedule to place the call now instead.`
  }

  const saved = savedEmail?.trim()
  const entered = recipient?.trim()
  const to = saved ? ` to ${saved}` : ''
  const differentRecipient =
    saved && entered && saved.toLowerCase() !== entered.toLowerCase()
      ? ` The scheduled email goes to ${saved}, not ${entered}.`
      : ''
  return `An automated email${to} ${when}. Reschedule it to send now instead. ${differentRecipient}`
}

interface Pending {
  voeId: string
  kind: ScheduledOutreachKind
  description: string
  manual: () => void
  onSentNow?: () => void
}

export const useScheduledOutreachGuard = () => {
  const client = useApolloClient()
  const [pending, setPending] = useState<Pending | null>(null)
  // Kept after close so the copy does not blank during the exit animation.
  const [shown, setShown] = useState<Pending | null>(null)
  const checkingRef = useRef(false)

  const [sendOutreachNow, { loading: sendingNow }] = useMutation(
    SEND_VOE_OUTREACH_NOW,
    { refetchQueries: 'active' }
  )

  const track = (
    name: 'voe_scheduled_outreach_prompted' | 'voe_scheduled_outreach_decided',
    target: Pending,
    outcome?: ScheduledOutreachOutcome
  ) =>
    datadogRum.addAction(name, {
      surface: 'dashboard',
      voe_id: target.voeId,
      outreach_type: target.kind,
      ...(outcome && { outcome }),
    })

  /**
   * Run `manual` now, or first ask when automation has the next `kind`
   * scheduled. `onSentNow` runs after the scheduled one was sent instead —
   * e.g. to close the email composer.
   */
  const guardScheduledOutreach = async (
    {
      voeId,
      kind,
      savedEmail,
      recipient,
    }: {
      voeId: string | null | undefined
      kind: ScheduledOutreachKind
      /** The VoE's saved email: where the scheduled email goes. */
      savedEmail?: string | null
      /** The address the recruiter is about to send a one-off to. */
      recipient?: string | null
    },
    { manual, onSentNow }: { manual: () => void; onSentNow?: () => void }
  ) => {
    if (!voeId) {
      manual()
      return
    }
    // One check per click: a second click while it runs is ignored.
    if (checkingRef.current) return
    checkingRef.current = true

    let next: ActionLike | null = null
    try {
      const { data } = await client.query({
        query: GET_VOE_ACTIONS,
        variables: { voeId },
        fetchPolicy: 'network-only',
      })
      next = nextScheduledAutomatedAction(data?.voeActions, kind)
    } catch {
      // Don't block the one-off on a failed check.
    } finally {
      checkingRef.current = false
    }

    if (!next) {
      manual()
      return
    }

    const target: Pending = {
      voeId,
      kind,
      description: describeScheduledOutreach({
        kind,
        scheduledAt: next.scheduledAt,
        savedEmail,
        recipient,
      }),
      manual,
      onSentNow,
    }
    setPending(target)
    setShown(target)
    track('voe_scheduled_outreach_prompted', target)
  }

  const decide = async (outcome: ScheduledOutreachOutcome) => {
    const target = pending
    if (!target || sendingNow) return

    if (outcome === 'sent_now') {
      try {
        const { data } = await sendOutreachNow({
          variables: { voeId: target.voeId, type: target.kind },
        })
        track('voe_scheduled_outreach_decided', target, outcome)
        setPending(null)
        // A call is held to the employer's calling hours, so "now" can land
        // on the next opening instead.
        const scheduledAt = data?.sendVoEOutreachNow?.scheduledAt
        const movedLater =
          scheduledAt &&
          new Date(scheduledAt).getTime() - Date.now() > 60 * 1000
        if (movedLater) {
          toast(
            `Calling hours are closed for this employer, so the call goes out at ${formatDateCustom(
              scheduledAt,
              DATE_TIME_FORMAT
            )}`,
            'warning'
          )
        } else {
          toast(COPY[target.kind].sentToast, 'success')
        }
        target.onSentNow?.()
      } catch (error) {
        // Stay open so the recruiter can choose again.
        toast((error as Error).message, 'error')
      }
      return
    }

    track('voe_scheduled_outreach_decided', target, outcome)
    setPending(null)
    if (outcome === 'manual') target.manual()
  }

  const copy = COPY[shown?.kind ?? 'EMAIL']

  const scheduledOutreachPrompt = (
    <Modal
      opened={pending != null}
      onClose={() => !sendingNow && decide('cancelled')}
      title={copy.title}
      centered
      size="md"
      // Above the email composer (210), which this opens over.
      zIndex={220}
      classNames={{ title: 'font-semibold' }}
      // The modal portals out, but React still bubbles its clicks through the
      // component tree — keep them from reaching a clickable row or card.
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      <div className="flex flex-col gap-5">
        <Text size="sm" className="text-doubleNickel-gray-700">
          {shown?.description}
        </Text>
        <div className="flex flex-row flex-wrap justify-end gap-3">
          <DNButton
            variant="transparent"
            disabled={sendingNow}
            onClick={() => decide('cancelled')}
          >
            Cancel
          </DNButton>
          <DNButton
            variant="outline"
            disabled={sendingNow}
            onClick={() => decide('manual')}
          >
            {copy.manual}
          </DNButton>
          <DNButton
            variant="primary"
            loading={sendingNow}
            onClick={() => decide('sent_now')}
          >
            {copy.sendNow}
          </DNButton>
        </div>
      </div>
    </Modal>
  )

  return { guardScheduledOutreach, scheduledOutreachPrompt }
}
